import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';
import FormModal from '../components/common/FormModal';
import ConfirmModal from '../components/common/ConfirmModal';

const { 
  FiTag, FiPlus, FiTrash2, FiZap, FiCalendar, FiDollarSign, 
  FiClock, FiSettings, FiCheck, FiTrendingUp, FiTarget, 
  FiArrowRight, FiShield, FiAlertCircle, FiPieChart, FiActivity,
  FiLayers, FiUsers, FiLock, FiUnlock
} = FiIcons;

export default function Monetization() {
  const { addToast } = useToast();
  const [coupons, setCoupons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tiers'); // 'tiers', 'coupons', 'strategies'
  
  // Modals
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const [isTierModalOpen, setIsTierModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const [couponsRes, coursesRes, tiersRes] = await Promise.all([
        supabase.from('coupons_2024').select('*').order('created_at', { ascending: false }),
        supabase.from('courses_2024').select('id, title, price'),
        supabase.from('pricing_tiers_2024').select('*, course:courses_2024(title)')
      ]);

      setCoupons(couponsRes.data || []);
      setCourses(coursesRes.data || []);
      setTiers(tiersRes.data || []);
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    const formData = new FormData(e.target);
    const payload = {
      code: formData.get('code').toUpperCase(),
      discount_type: formData.get('discount_type'),
      value: parseFloat(formData.get('value')),
      max_uses: formData.get('max_uses') ? parseInt(formData.get('max_uses')) : null,
      expires_at: formData.get('expires_at') || null,
      is_active: true
    };

    try {
      const { error } = await supabase.from('coupons_2024').insert([payload]);
      if (error) throw error;
      addToast("Coupon yield strategy activated");
      setIsCouponModalOpen(false);
      fetchData();
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleCreateTier = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    const formData = new FormData(e.target);
    
    // Dynamic Rules Logic (Simplified for UI)
    const dynamic_rules = [];
    if (formData.get('auto_escalate') === 'on') {
      dynamic_rules.push({
        type: 'price_increase',
        threshold: parseInt(formData.get('threshold')),
        amount: parseFloat(formData.get('increase_amount'))
      });
    }

    const payload = {
      course_id: formData.get('course_id'),
      tier_name: formData.get('tier_name'),
      price: parseFloat(formData.get('price')),
      max_quantity: formData.get('max_quantity') ? parseInt(formData.get('max_quantity')) : null,
      display_label: formData.get('display_label'),
      active_from: formData.get('active_from') || null,
      active_until: formData.get('active_until') || null,
      dynamic_rules: dynamic_rules,
      is_default: formData.get('is_default') === 'on'
    };

    try {
      const { error } = await supabase.from('pricing_tiers_2024').insert([payload]);
      if (error) throw error;
      addToast("Dynamic pricing tier published");
      setIsTierModalOpen(false);
      fetchData();
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const deleteItem = async (table, id) => {
    try {
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      addToast("Strategy decommissioned");
      fetchData();
    } catch (error) {
      addToast(error.message, 'error');
    }
  };

  if (loading) return (
    <div className="h-[60vh] flex flex-col items-center justify-center space-y-6">
      <div className="w-16 h-16 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
      <p className="text-slate-500 font-black text-[10px] uppercase tracking-[0.3em]">Calibrating Revenue Engine...</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-12 pb-32">
      {/* Premium Header */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 bg-slate-900/40 backdrop-blur-3xl p-10 rounded-[3rem] border border-white/5 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/5 blur-[120px] rounded-full -mr-48 -mt-48" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 border border-emerald-500/20">
              <SafeIcon icon={FiDollarSign} />
            </div>
            <span className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.4em]">Monetization Alpha</span>
          </div>
          <h1 className="text-5xl font-black text-white tracking-tighter uppercase leading-none">Revenue Engine</h1>
          <p className="text-slate-400 mt-4 text-lg font-medium max-w-xl leading-relaxed">Systematic yield management through dynamic pricing, tiered offerings, and behavioral discounts.</p>
        </div>
        
        <div className="flex gap-4 relative z-10">
          <button 
            onClick={() => { setSelectedItem(null); setIsCouponModalOpen(true); }}
            className="bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-white/10 transition-all flex items-center gap-3"
          >
            <SafeIcon icon={FiPlus} /> New Coupon
          </button>
          <button 
            onClick={() => { setSelectedItem(null); setIsTierModalOpen(true); }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-2xl shadow-emerald-600/30 transition-all flex items-center gap-3"
          >
            <SafeIcon icon={FiTrendingUp} /> Create Tier
          </button>
        </div>
      </header>

      {/* Analytics Insight */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'MTD Revenue', val: '$42,850', icon: FiTrendingUp, color: 'text-emerald-400', trend: '+18.4%' },
          { label: 'Avg Order Value', val: '$342', icon: FiTarget, color: 'text-indigo-400', trend: '+5.2%' },
          { label: 'Yield Efficiency', val: '94%', icon: FiZap, color: 'text-amber-400', trend: 'OPTIMAL' },
          { label: 'Churn Risk', val: '2.8%', icon: FiActivity, color: 'text-rose-400', trend: '-1.1%' }
        ].map((stat, i) => (
          <div key={i} className="bg-white/[0.02] border border-white/10 p-8 rounded-[2.5rem] flex items-center justify-between backdrop-blur-xl group hover:border-white/20 transition-all">
             <div>
               <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">{stat.label}</p>
               <h3 className="text-3xl font-black text-white tracking-tight">{stat.val}</h3>
               <p className={`text-[8px] font-bold mt-1 ${stat.trend.startsWith('+') ? 'text-emerald-400' : stat.trend === 'OPTIMAL' ? 'text-indigo-400' : 'text-rose-400'}`}>
                 {stat.trend} <span className="text-slate-600 ml-1">vs last block</span>
               </p>
             </div>
             <div className={`w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center ${stat.color} group-hover:scale-110 transition-transform border border-white/5`}>
               <SafeIcon icon={stat.icon} className="text-xl" />
             </div>
          </div>
        ))}
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-3 p-2 bg-white/[0.02] border border-white/10 rounded-[2rem] w-fit">
        {[
          { id: 'tiers', label: 'Pricing Tiers', icon: FiLayers },
          { id: 'coupons', label: 'Discount Protocols', icon: FiTag },
          { id: 'strategies', label: 'Dynamic Rules', icon: FiZap }
        ].map(tab => (
          <button 
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-8 py-4 rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-3 ${activeTab === tab.id ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-600/20' : 'text-slate-500 hover:text-slate-300'}`}
          >
            <SafeIcon icon={tab.icon} /> {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'tiers' ? (
          <motion.div 
            key="tiers"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {tiers.map((tier, idx) => (
              <motion.div 
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white/[0.02] border border-white/10 p-10 rounded-[3.5rem] relative overflow-hidden group backdrop-blur-3xl transition-all hover:bg-white/[0.05] hover:border-white/20"
              >
                {tier.is_default && (
                  <div className="absolute top-8 right-8 px-3 py-1 bg-indigo-500/20 border border-indigo-500/30 rounded-full flex items-center gap-2">
                    <SafeIcon icon={FiCheck} className="text-indigo-400 text-[10px]" />
                    <span className="text-[8px] font-black text-indigo-400 uppercase tracking-widest">Primary Tier</span>
                  </div>
                )}

                <div className="space-y-6 mb-12">
                   <p className="text-[9px] font-black text-slate-500 uppercase tracking-[0.3em]">{tier.course?.title || 'Generic Tier'}</p>
                   <h3 className="text-3xl font-black text-white tracking-tighter uppercase leading-tight">{tier.tier_name}</h3>
                   <div className="flex items-baseline gap-2">
                     <span className="text-5xl font-black text-white">${tier.price}</span>
                     {tier.display_label && <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest">{tier.display_label}</span>}
                   </div>
                </div>

                <div className="space-y-4 mb-10">
                  <div className="flex justify-between items-center text-[10px] font-bold">
                    <span className="text-slate-500 uppercase tracking-widest">Enrollment Status</span>
                    <span className="text-white">{tier.current_quantity} / {tier.max_quantity || '∞'}</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden border border-white/5">
                    <div 
                      className="h-full bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)]" 
                      style={{ width: `${tier.max_quantity ? (tier.current_quantity / tier.max_quantity) * 100 : 0}%` }}
                    />
                  </div>
                </div>

                <div className="pt-8 border-t border-white/5 flex justify-between items-center">
                   <div className="flex gap-2">
                     {tier.dynamic_rules?.length > 0 && (
                       <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400" title="Dynamic Rules Active">
                         <SafeIcon icon={FiZap} />
                       </div>
                     )}
                     {tier.active_until && (
                       <div className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400" title="Time Limited">
                         <SafeIcon icon={FiClock} />
                       </div>
                     )}
                   </div>
                   <button onClick={() => deleteItem('pricing_tiers_2024', tier.id)} className="w-10 h-10 bg-white/5 hover:bg-rose-500/10 rounded-xl flex items-center justify-center text-slate-500 hover:text-rose-400 transition-all">
                     <SafeIcon icon={FiTrash2} />
                   </button>
                </div>
              </motion.div>
            ))}
            
            <button 
              onClick={() => setIsTierModalOpen(true)}
              className="border-2 border-dashed border-white/10 rounded-[3.5rem] p-10 flex flex-col items-center justify-center gap-4 hover:border-emerald-500/40 hover:bg-emerald-500/[0.02] transition-all group"
            >
              <div className="w-16 h-16 rounded-[2rem] bg-white/5 flex items-center justify-center text-slate-500 group-hover:text-emerald-400 transition-all border border-white/5">
                <SafeIcon icon={FiPlus} className="text-2xl" />
              </div>
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest group-hover:text-white transition-all">Architect New Tier</p>
            </button>
          </motion.div>
        ) : activeTab === 'coupons' ? (
          <motion.div 
            key="coupons"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white/[0.02] border border-white/10 rounded-[3rem] overflow-hidden backdrop-blur-3xl"
          >
            <div className="p-8 border-b border-white/5 flex justify-between items-center bg-white/[0.01]">
               <h3 className="text-lg font-black text-white uppercase tracking-tight flex items-center gap-3">
                 <SafeIcon icon={FiTag} className="text-emerald-400" /> Active Discount Protocols
               </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Protocol Code</th>
                    <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Yield Impact</th>
                    <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Conversion Metrics</th>
                    <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Status</th>
                    <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr key={coupon.id} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                           <div className="px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-mono font-black text-sm tracking-widest">
                             {coupon.code}
                           </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <p className="text-sm text-white font-bold">
                          {coupon.discount_type === 'percentage' ? `${coupon.value}% OFF` : `$${coupon.value} FLAT`}
                        </p>
                        <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest mt-1">Direct Rev Reduction</p>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-3">
                          <span className="text-[11px] font-black text-white uppercase tracking-tighter">{coupon.used_count} / {coupon.max_uses || '∞'}</span>
                          <div className="w-20 h-1 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500" style={{ width: `${coupon.max_uses ? (coupon.used_count / coupon.max_uses) * 100 : 40}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border border-white/5 inline-flex items-center gap-2 ${
                          coupon.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                        }`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${coupon.is_active ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
                          {coupon.is_active ? 'Protocol Active' : 'Deactivated'}
                        </div>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <button onClick={() => deleteItem('coupons_2024', coupon.id)} className="p-2 text-slate-500 hover:text-rose-400 transition-all opacity-0 group-hover:opacity-100">
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        ) : (
          <motion.div 
            key="strategies"
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {[
              { 
                title: 'Scarcity Engine', 
                desc: 'Automatically increase price by $50 for every 10 seats sold. Drives immediate FOMO.', 
                icon: FiZap, 
                color: 'text-amber-400',
                status: 'READY'
              },
              { 
                title: 'Early Bird Warp', 
                desc: 'Time-decay pricing. Discount reduces by 5% every 24 hours until launch.', 
                icon: FiClock, 
                color: 'text-indigo-400',
                status: 'EXPERIMENTAL'
              },
              { 
                title: 'Bulk Logic', 
                desc: 'Volume-based scaling. 20% discount applied when 3+ licenses are purchased.', 
                icon: FiUsers, 
                color: 'text-emerald-400',
                status: 'LIVE'
              },
              { 
                title: 'Waitlist Priority', 
                desc: 'Grant exclusive 48h access to specific pricing tiers for tagged leads.', 
                icon: FiLock, 
                color: 'text-purple-400',
                status: 'LOCKED'
              }
            ].map((strat, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/10 p-10 rounded-[3.5rem] flex items-start gap-8 group hover:bg-white/[0.04] transition-all relative overflow-hidden">
                 <div className={`w-20 h-20 min-w-[5rem] rounded-[2rem] bg-white/5 border border-white/10 flex items-center justify-center ${strat.color} text-3xl group-hover:scale-110 transition-transform`}>
                   <SafeIcon icon={strat.icon} />
                 </div>
                 <div className="flex-1">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-xl font-black text-white uppercase tracking-tighter">{strat.title}</h4>
                      <span className="text-[8px] font-black px-2 py-1 bg-white/5 rounded border border-white/5 text-slate-500 uppercase tracking-widest">{strat.status}</span>
                    </div>
                    <p className="text-slate-400 text-sm font-medium leading-relaxed mb-6">{strat.desc}</p>
                    <button className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] flex items-center gap-2 group/btn hover:text-white transition-all">
                      Configure Strategy <SafeIcon icon={FiArrowRight} className="group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                 </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tier Builder Modal */}
      <FormModal 
        isOpen={isTierModalOpen} 
        onClose={() => setIsTierModalOpen(false)} 
        title="Architect Pricing Tier" 
        onSubmit={handleCreateTier}
        loading={modalLoading}
        width="max-w-4xl"
      >
        <div className="space-y-12 max-h-[75vh] overflow-y-auto pr-6 custom-scrollbar pb-10">
          <section className="space-y-8">
            <div className="flex items-center gap-4 border-b border-white/5 pb-4">
              <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                <SafeIcon icon={FiTarget} />
              </div>
              <h3 className="text-lg font-black text-white uppercase tracking-tight">Identity & Course</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Course Target</label>
                <select name="course_id" required className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-emerald-500/50 appearance-none">
                  {courses.map(c => <option key={c.id} value={c.id}>{c.title.toUpperCase()}</option>)}
                </select>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Tier Identity</label>
                <input name="tier_name" required className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-emerald-500/50" placeholder="e.g. VIP Mastermind" />
              </div>
            </div>
          </section>

          <section className="space-y-8">
            <div className="flex items-center gap-4 border-b border-white/5 pb-4">
              <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                <SafeIcon icon={FiDollarSign} />
              </div>
              <h3 className="text-lg font-black text-white uppercase tracking-tight">Pricing & Yield</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Base Price ($)</label>
                <input name="price" type="number" required className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-emerald-500/50" placeholder="997" />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Seat Limit</label>
                <input name="max_quantity" type="number" className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-emerald-500/50" placeholder="Empty = ∞" />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Promo Label</label>
                <input name="display_label" className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-emerald-500/50" placeholder="e.g. Best Value" />
              </div>
            </div>
          </section>

          <section className="space-y-8 p-10 bg-emerald-500/5 border border-emerald-500/20 rounded-[2.5rem]">
            <div className="flex items-center justify-between border-b border-white/10 pb-6 mb-6">
               <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                    <SafeIcon icon={FiZap} />
                  </div>
                  <h3 className="text-lg font-black text-white uppercase tracking-tight">Yield Maximization Rules</h3>
               </div>
               <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" name="auto_escalate" className="sr-only peer" />
                  <div className="w-14 h-7 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
               </label>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 opacity-40 peer-checked:opacity-100 transition-opacity">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Sales Threshold</label>
                <input name="threshold" type="number" className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none" placeholder="Every 10 sales..." />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Price Increase ($)</label>
                <input name="increase_amount" type="number" className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none" placeholder="+ $50.00" />
              </div>
            </div>
          </section>
        </div>
      </FormModal>

      {/* Coupon Modal */}
      <FormModal 
        isOpen={isCouponModalOpen} 
        onClose={() => setIsCouponModalOpen(false)} 
        title="Design Discount Protocol" 
        onSubmit={handleCreateCoupon}
        loading={modalLoading}
        width="max-w-2xl"
      >
        <div className="space-y-8 pb-8">
          <div className="space-y-3">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Strategic Code</label>
            <input name="code" required className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-mono font-black outline-none focus:ring-2 focus:ring-emerald-500/50 uppercase tracking-widest" placeholder="FLASHSALE25" />
          </div>
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Impact Type</label>
              <select name="discount_type" className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none appearance-none">
                <option value="percentage">PERCENTAGE (%)</option>
                <option value="fixed">FLAT AMOUNT ($)</option>
              </select>
            </div>
            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Effect Value</label>
              <input name="value" type="number" required className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none" placeholder="20" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Usage Capacity</label>
              <input name="max_uses" type="number" className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none" placeholder="Unlimited" />
            </div>
            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Expiration</label>
              <input name="expires_at" type="date" className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none" />
            </div>
          </div>
        </div>
      </FormModal>

      {/* Floating System Status */}
      <motion.div 
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 px-10 py-5 bg-slate-900/80 backdrop-blur-3xl border border-white/10 rounded-full shadow-2xl flex items-center gap-8"
      >
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          <span className="text-[10px] font-black text-white uppercase tracking-widest">Yield Processor Online</span>
        </div>
        <div className="h-5 w-px bg-white/10" />
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiShield} className="text-emerald-400" />
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">SSL Transaction Vault</span>
        </div>
      </motion.div>
    </div>
  );
}